/**
 * Pure collectors that turn raw app state into the scenario `settings/`
 * artifacts. No stores, no DOM, no zip — every input is passed in explicitly so
 * the logic is trivially unit-testable. The store/DOM wiring lives in the
 * adapter (`settingsSource.ts`).
 */
import { SCENARIO_SCHEMA_VERSION, type EnvironmentInfo } from '../contract';
import type { EnvironmentDocument, FeatureFlags, WebExportSettings, WebSettings } from './settingsContract';

const UNKNOWN = 'unknown';

export interface WebSettingsInput {
  language?: string | null;
  units?: string | null;
  filters?: Record<string, unknown> | null;
  sort?: string | null;
  search?: string | null;
  export?: WebExportSettings | null;
}

/** Project the active UI settings into the `web-settings.json` shape. */
export function collectWebSettings(input: WebSettingsInput = {}): WebSettings {
  return {
    language: input.language ?? null,
    units: input.units ?? null,
    filters: input.filters ? { ...input.filters } : null,
    sort: input.sort ?? null,
    search: input.search ?? null,
    export: input.export ? { ...input.export } : null,
  };
}

export type FeatureFlagsInput = Record<string, unknown>;

/**
 * Normalize the active feature toggles: drop `undefined` entries and emit the
 * keys in a deterministic (sorted) order so the artifact is diff-stable.
 */
export function collectFeatureFlags(input: FeatureFlagsInput = {}): FeatureFlags {
  const out: FeatureFlags = {};
  for (const key of Object.keys(input).sort()) {
    const value = input[key];
    if (value !== undefined) {
      out[key] = value;
    }
  }
  return out;
}

export interface EnvironmentInput {
  appVersion?: string | null;
  webClientVersion?: string | null;
  revitPluginVersion?: string | null;
  revitVersion?: string | null;
  os?: string | null;
}

function nonEmpty(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : UNKNOWN;
}

/**
 * Build the self-describing `environment.json` document. Missing/blank versions
 * fall back to `"unknown"` so the artifact never carries empty strings (and the
 * inline {@link EnvironmentInfo} stays valid against the schema's minLength: 1).
 */
export function collectEnvironment(input: EnvironmentInput = {}): EnvironmentDocument {
  return {
    schemaVersion: SCENARIO_SCHEMA_VERSION,
    appVersion: nonEmpty(input.appVersion),
    webClientVersion: nonEmpty(input.webClientVersion),
    revitPluginVersion: nonEmpty(input.revitPluginVersion),
    revitVersion: nonEmpty(input.revitVersion),
    os: nonEmpty(input.os),
  };
}

/** Strip the `schemaVersion` tag to the inline {@link EnvironmentInfo} for scenario.json. */
export function toEnvironmentInfo(document: EnvironmentDocument): EnvironmentInfo {
  const { schemaVersion: _schemaVersion, ...info } = document;
  return info;
}
